import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const LEGAL = [
  { slug: "privacy", title: "Privacy Policy" },
  { slug: "terms", title: "Terms & Conditions" },
  { slug: "refund", title: "Refund Policy" },
  { slug: "contact", title: "Contact" },
  { slug: "faq", title: "FAQ" },
];

export default function AdminSettings() {
  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Settings</h1>
      <Tabs defaultValue="general" className="mt-6">
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="general">General & Analytics</TabsTrigger>
          <TabsTrigger value="legal">Legal Pages</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-6"><GeneralSettings /></TabsContent>
        <TabsContent value="legal" className="mt-6"><LegalSettings /></TabsContent>
      </Tabs>
    </div>
  );
}

function GeneralSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => { await api.put("/admin/settings", s); toast.success("Settings saved"); };
  return (
    <div className="space-y-4 rounded-lg border border-white/10 bg-zinc-900/40 p-5">
      <div><Label className="text-xs uppercase text-zinc-400">Site Name</Label><Input value={s.site_name || ""} onChange={(e) => set("site_name", e.target.value)} className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
      <div><Label className="text-xs uppercase text-zinc-400">Google Analytics Measurement ID</Label><Input placeholder="G-XXXXXXX" value={s.ga_measurement_id || ""} onChange={(e) => set("ga_measurement_id", e.target.value)} className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
      <div><Label className="text-xs uppercase text-zinc-400">Google Tag Manager ID</Label><Input placeholder="GTM-XXXXXX" value={s.gtm_id || ""} onChange={(e) => set("gtm_id", e.target.value)} className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
      <div><Label className="text-xs uppercase text-zinc-400">Contact Email</Label><Input placeholder="you@epicfade.com" value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-1 bg-zinc-900 border-white/10 text-white" data-testid="settings-contact-email" /></div>
      <div><Label className="text-xs uppercase text-zinc-400">Instagram URL</Label><Input placeholder="https://instagram.com/yourhandle" value={s.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} className="mt-1 bg-zinc-900 border-white/10 text-white" data-testid="settings-instagram-url" /></div>
      <Button data-testid="save-settings-btn" onClick={save} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Save className="mr-1 h-4 w-4" /> Save</Button>
    </div>
  );
}

function LegalSettings() {
  const [slug, setSlug] = useState("privacy");
  const [page, setPage] = useState(null);
  useEffect(() => { api.get(`/legal/${slug}`).then(({ data }) => setPage(data)).catch(() => setPage({ slug, title: LEGAL.find((l) => l.slug === slug)?.title, content: "" })); }, [slug]);
  const save = async () => { await api.put(`/admin/legal/${slug}`, page); toast.success("Page saved"); };
  return (
    <div className="space-y-4">
      <Select value={slug} onValueChange={setSlug}>
        <SelectTrigger className="max-w-xs bg-zinc-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-zinc-950 border-white/10 text-white">{LEGAL.map((l) => <SelectItem key={l.slug} value={l.slug}>{l.title}</SelectItem>)}</SelectContent>
      </Select>
      {!page ? <Loader2 className="h-6 w-6 animate-spin text-[#DCA846]" /> : (
        <div className="space-y-4 rounded-lg border border-white/10 bg-zinc-900/40 p-5">
          <div><Label className="text-xs uppercase text-zinc-400">Title</Label><Input value={page.title || ""} onChange={(e) => setPage({ ...page, title: e.target.value })} className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
          <div><Label className="text-xs uppercase text-zinc-400">Content (HTML supported)</Label><Textarea rows={14} value={page.content || ""} onChange={(e) => setPage({ ...page, content: e.target.value })} className="mt-1 bg-zinc-900 border-white/10 font-mono text-sm text-white" /></div>
          <Button data-testid="save-legal-btn" onClick={save} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Save className="mr-1 h-4 w-4" /> Save Page</Button>
        </div>
      )}
    </div>
  );
}
